// components/AsymmetryCard/AsymmetryCard.tsx
'use client';
import React, { useState } from 'react';

import Bookmaker from '@src/components/Icons/Bookmaker';
import CardMetric from '@src/components/Shared/CardMetric';
import NextLink from '@src/components/Shared/NextLink';
import FractionDisplay from '@src/components/Shared/FractionDisplay';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';
import { AsymmetryCardPropTypes } from './AsymmetryCard.types';

const calculationMethods: UnitTypes[] = [
  { id: 'vald', name: 'VALD Asymmetry Formula', unit: 'N' },
  { id: 'lsi', name: 'Limb Symmetry Index', unit: 'N' },
  { id: 'si', name: 'Symmetry Index', unit: 'N' },
];

const AsymmetryCard: React.FC<AsymmetryCardPropTypes> = () => {
  const isMobile = useIsMobile();

  // --- input state ---
  const [rightValue, setRightValue] = useState('');
  const [leftValue, setLeftValue] = useState('');
  const [tempRightValue, setTempRightValue] = useState('');
  const [tempLeftValue, setTempLeftValue] = useState('');
  const [needsCalculation, setNeedsCalculation] = useState(false);

  const handleCalculate = () => {
    setRightValue(tempRightValue);
    setLeftValue(tempLeftValue);
    setNeedsCalculation(false);
  };

  // --- formula selection state ---
  const [formula, setFormula] = useState<'vald' | 'lsi' | 'si'>('vald');

  // pick values based on mobile vs desktop
  const currentRightVal = isMobile && needsCalculation ? tempRightValue : rightValue;
  const currentLeftVal = isMobile && needsCalculation ? tempLeftValue : leftValue;
  const rightVal = parseFloat(currentRightVal) || 0;
  const leftVal = parseFloat(currentLeftVal) || 0;
  const higherVal = Math.max(rightVal, leftVal);

  const computeAsymmetry = () => {
    switch (formula) {
      case 'vald':
        return higherVal === 0 ? 0 : Math.abs((rightVal - leftVal) / higherVal) * 100;
      case 'lsi': {
        const denom = Math.max(rightVal, leftVal);
        return denom === 0 ? 0 : (Math.min(rightVal, leftVal) / denom) * 100;
      }
      case 'si': {
        const num = Math.abs(rightVal - leftVal);
        const denom = rightVal + leftVal;
        return denom === 0 ? 0 : (num / denom) * 100;
      }
      default:
        return 0;
    }
  };

  const asymmetry = computeAsymmetry();
  const formattedAsymmetry = asymmetry.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // --- Debounce the formatted value by 3000 ms ---
  const debouncedAsymmetry = useDebounce(formattedAsymmetry, 3000);

  // Fire a tracking event whenever the debounced result settles
  React.useEffect(() => {
    // Only track if both inputs are non‐empty and debouncedAsymmetry is a valid number
    if (
      currentRightVal !== '' &&
      currentLeftVal !== '' &&
      !isNaN(Number(debouncedAsymmetry)) &&
      isFinite(Number(debouncedAsymmetry))
    ) {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_asymmetry',
          value: 1,
        });
      }
    }
    // We only depend on debouncedAsymmetry here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAsymmetry]);

  // labels based on formula
  let numeratorLabel = 'Right Value - Left Value';
  let denominatorLabel = 'Higher Value';
  let fractionLabel = 'Asymmetry';

  if (formula === 'lsi') {
    numeratorLabel = 'min(Right, Left)';
    denominatorLabel = 'max(Right, Left)';
    fractionLabel = 'Limb Symmetry Index';
  } else if (formula === 'si') {
    numeratorLabel = 'max(Right, Left) - min(Right, Left)';
    denominatorLabel = 'max(Right, Left) + min(Right, Left)';
    fractionLabel = 'Symmetry Index';
  }

  return (
    <CardMetric
      id='calculator-asymmetry'
      title='Asymmetry'
      productIcons={[
        {
          alt: 'HumanTrak App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/XjLSuUERWGGH5l8YUIGK5/4d79255d0480499ea0c5684e2ea2f1fc/humantrack-app-icon.svg',
        },
        {
          alt: 'NordBord App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/6iS357n4siVSm6nFZeci6q/544cd567518357e813ac48f15dae44f4/nordbord-app-icon.svg',
        },
        {
          alt: 'ForceFrame App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/7dxABhvlBmXLF0UuYpAdss/883fc648f9bc2f93d9d05d9095800e67/forceframe-app-icon.svg',
        },
        {
          alt: 'DynaMo App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/5KQtYsCw5caSZvIuohrRmm/c174bb8572e3170009776e40c21f18e9/dynamo-app-icon.svg',
        },
        {
          alt: 'ForceDecks App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/1sZL8SrOEPwzDxDhnJCoy2/d3561b92560bdd3fe2ecd709b6f4604f/forcedecks-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2rem] text-[1.2rem]'>
        Select from a variety of kinetic and kinematic metrics to quantify client asymmetry
        percentages for important rehab and performance values.
      </p>

      <div className='mb-[2.4rem] flex flex-col gap-[0.5rem] sm:flex-row sm:items-center'>
        <UnitDropdown
          className='mb-[0]!'
          units={calculationMethods}
          label=''
          activeUnitId={formula}
          onChange={(val) => setFormula(val as 'vald' | 'lsi' | 'si')}
        />

        <FractionDisplay
          label={fractionLabel}
          numerator={`<strong className="font-medium">${numeratorLabel}</strong>`}
          denominator={`<strong className="font-medium">${denominatorLabel}</strong>`}
          multiplier='× 100'
        />
      </div>

      {/* inputs */}
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Right Side', 'Value']}
        activeUnit='unit'
        value={isMobile ? tempRightValue : rightValue}
        onChange={(e) => {
          if (isMobile) {
            setTempRightValue(e.target.value);
            setNeedsCalculation(true);
          } else {
            setRightValue(e.target.value);
          }
        }}
      />
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Left Side', 'Value']}
        activeUnit='unit'
        value={isMobile ? tempLeftValue : leftValue}
        onChange={(e) => {
          if (isMobile) {
            setTempLeftValue(e.target.value);
            setNeedsCalculation(true);
          } else {
            setLeftValue(e.target.value);
          }
        }}
      />

      {/* result */}
      <div className='mb-[3.1rem] flex items-center justify-between font-normal text-primary'>
        <span className='ml-auto text-[1.4rem] font-medium'>{fractionLabel}</span>
        <span className='ml-[2rem] border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          {isMobile && needsCalculation
            ? '0.0'
            : isNaN(asymmetry) || !isFinite(asymmetry)
              ? '0.0'
              : formattedAsymmetry}
          <span className='text-[1.6rem] font-light'>%</span>
        </span>
      </div>

      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />

        <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />

          <p className='text-pretty'>
            Explore an{' '}
            <NextLink
              className='font-light underline'
              href='https://www.globalperformanceinsights.com/post/measuring-athlete-asymmetries-the-calculation-conundrum'
            >
              in-depth explanation
            </NextLink>{' '}
            of various asymmetry calculation methods.
          </p>
        </div>

        {/* mobile calculate button */}
        {isMobile && (
          <button
            onClick={handleCalculate}
            className='common-btn-transition right-0 bottom-0 relative ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default AsymmetryCard;
